(function() {
  const BASE_URL = 'https://api.okendo.io/v1';
  const REVIEWS_LIMIT = 3;
  const REVIEW_BODY_LENGTH = 120;
  const productDataCache = new Map();

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function(char) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[char];
    });
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return month + '/' + day + '/' + date.getUTCFullYear();
  }

  function starMarkup(className) {
    return '<svg class="' + escapeHtml(className) + '" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 17 16" fill="none"><path d="M8.14453 0L10.0117 5.7465H16.0539L11.1656 9.29803L13.0328 15.0445L8.14453 11.493L3.25627 15.0445L5.12342 9.29803L0.235159 5.7465H6.27738L8.14453 0Z" fill="#323232"/></svg>';
  }

  function verifiedMarkup() {
    return '<svg aria-hidden="true" focusable="false" width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.8488 8.47254C15.8488 9.1105 14.642 9.58366 14.4825 10.1738C14.323 10.7639 15.1258 11.7953 14.8174 12.3322C14.5091 12.8692 13.2225 12.6831 12.7812 13.119C12.34 13.555 12.5367 14.8415 11.9944 15.1552C11.4522 15.4688 10.4474 14.6608 9.836 14.8202C9.22462 14.9797 8.77273 16.1865 8.13478 16.1865C7.49682 16.1865 7.02366 14.9797 6.43355 14.8202C5.84344 14.6608 4.81207 15.4635 4.27512 15.1552C3.73817 14.8468 3.92424 13.5603 3.4883 13.119C3.05236 12.6778 1.76581 12.8745 1.45214 12.3322C1.13848 11.7899 1.94656 10.7851 1.78707 10.1738C1.62758 9.56239 0.420776 9.1105 0.420776 8.47254C0.420776 7.83458 1.62758 7.36143 1.78707 6.77132C1.94656 6.1812 1.1438 5.14984 1.45214 4.61289C1.76049 4.07594 3.04704 4.26201 3.4883 3.82607C3.92956 3.39013 3.73285 2.10358 4.27512 1.78991C4.81738 1.47625 5.82217 2.28433 6.43355 2.12484C7.04493 1.96535 7.49682 0.758545 8.13478 0.758545C8.77273 0.758545 9.24589 1.96535 9.836 2.12484C10.4261 2.28433 11.4575 1.48157 11.9944 1.78991C12.5314 2.09826 12.3453 3.38481 12.7812 3.82607C13.2172 4.26732 14.5037 4.07062 14.8174 4.61289C15.1311 5.15515 14.323 6.15994 14.4825 6.77132C14.642 7.3827 15.8488 7.83458 15.8488 8.47254Z" fill="#6C685F"/><path d="M10.8408 7.54219C11.1066 7.27637 11.1066 6.85106 10.8408 6.58525C10.575 6.31943 10.1497 6.31943 9.88385 6.58525L7.54466 8.92443L6.38039 7.76016C6.11457 7.49434 5.68926 7.49434 5.42345 7.76016C5.15763 8.02597 5.15763 8.45128 5.42345 8.71709L7.06619 10.3598C7.19378 10.4874 7.36391 10.5565 7.54466 10.5565C7.72542 10.5565 7.89554 10.4874 8.02313 10.3598L10.8355 7.54219H10.8408Z" fill="white"/></svg>';
  }

  function normalizedProductId(value) {
    return String(value || '').replace(/^gid:\/\/shopify\/Product\//, '').replace(/^shopify-/, '').trim();
  }

  function averageRating(reviewAggregate) {
    const levels = reviewAggregate?.reviewCountByLevel || {};
    const count = Number(reviewAggregate?.reviewCount || 0);
    if (!count) return '';

    const total =
      Number(levels.level1Count || 0) * 1 +
      Number(levels.level2Count || 0) * 2 +
      Number(levels.level3Count || 0) * 3 +
      Number(levels.level4Count || 0) * 4 +
      Number(levels.level5Count || 0) * 5;

    return (total / count).toFixed(1);
  }

  function prepareReviewsData(data) {
    const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
    const hasBadRating = reviews.some(function(review) {
      return Number(review?.rating || 0) < 3;
    });

    if (!hasBadRating) {
      return {
        nextUrl: data?.nextUrl || '',
        reviews: reviews
      };
    }

    return {
      nextUrl: '',
      reviews: reviews.filter(function(review) {
        return Number(review?.rating || 0) >= 3;
      })
    };
  }

  function fetchJson(url) {
    return fetch(url, {method: 'GET'}).then(function(response) {
      if (!response.ok) return null;
      return response.json();
    }).catch(function() {
      return null;
    });
  }

  function fetchProductReviews(clientId, productId) {
    const normalizedId = normalizedProductId(productId);
    const key = clientId + ':' + normalizedId;
    if (productDataCache.has(key)) return productDataCache.get(key);

    const request = Promise.all([
      fetchJson(BASE_URL + '/stores/' + encodeURIComponent(clientId) + '/products/shopify-' + encodeURIComponent(normalizedId) + '/review_aggregate'),
      fetchJson(BASE_URL + '/stores/' + encodeURIComponent(clientId) + '/products/shopify-' + encodeURIComponent(normalizedId) + '/reviews?limit=' + REVIEWS_LIMIT + '&orderBy=rating%20desc')
    ]).then(function(results) {
      return {
        aggregate: results[0]?.reviewAggregate || null,
        reviewsData: prepareReviewsData(results[1] || {})
      };
    });

    productDataCache.set(key, request);
    return request;
  }

  function renderStars(container, rating, classPrefix) {
    if (!container) return;
    const amount = Math.max(0, Math.min(5, Math.round(Number(rating || 0))));
    const starClass = classPrefix + '__star';
    container.innerHTML = Array.from({length: amount}).map(function() {
      return starMarkup(starClass);
    }).join('');
  }

  function updateSummary(summary, aggregate) {
    const count = Number(aggregate?.reviewCount || 0);
    const average = averageRating(aggregate);
    const classPrefix = summary.dataset.okendoClassPrefix || 'karst-pdp-atf';
    if (!count || !average) {
      summary.dataset.okendoSummaryState = 'empty';
      summary.setAttribute('aria-busy', 'false');
      summary.removeAttribute('aria-label');
      return;
    }

    const countEl = summary.querySelector('[data-karst-okendo-count]');
    const averageEl = summary.querySelector('[data-karst-okendo-average]');
    if (countEl) countEl.textContent = String(count);
    if (averageEl) averageEl.textContent = '(' + average + ')';
    renderStars(summary.querySelector('[data-karst-okendo-stars]'), average, classPrefix);
    summary.setAttribute('aria-label', average + ' out of 5 stars based on ' + count + ' reviews');
    summary.dataset.okendoSummaryState = 'ready';
    summary.setAttribute('aria-busy', 'false');
  }

  function reviewCard(review, classPrefix) {
    const rating = Math.max(0, Math.min(5, Math.round(Number(review?.rating || 0))));
    const body = String(review?.body || '');
    const needsReadMore = body.length > REVIEW_BODY_LENGTH;
    const visibleBody = needsReadMore ? body.slice(0, REVIEW_BODY_LENGTH) + '...' : body;
    const verified = review?.reviewer?.isVerified
      ? '<p class="' + classPrefix + '__verified">' + verifiedMarkup() + 'Verified</p>'
      : '';

    return '<article class="' + classPrefix + '__review-card" data-karst-okendo-review-card>' +
      '<div class="' + classPrefix + '__review-top">' +
        '<div>' +
          '<p class="' + classPrefix + '__reviewer">' + escapeHtml(review?.reviewer?.displayName || '') + '</p>' +
          verified +
        '</div>' +
        '<time class="' + classPrefix + '__review-date">' + escapeHtml(formatDate(review?.dateCreated)) + '</time>' +
      '</div>' +
      '<h3 class="' + classPrefix + '__review-title">' + escapeHtml(review?.title || '') + '</h3>' +
      '<div class="' + classPrefix + '__review-stars" aria-label="' + rating + ' out of 5 stars">' +
        Array.from({length: rating}).map(function() { return starMarkup(classPrefix + '__star'); }).join('') +
      '</div>' +
      '<p class="' + classPrefix + '__review-body" data-karst-okendo-review-body data-full-body="' + escapeHtml(body) + '">' +
        escapeHtml(visibleBody) +
        (needsReadMore ? ' <button type="button" class="' + classPrefix + '__review-read-more" data-karst-okendo-read-more>Read more</button>' : '') +
      '</p>' +
    '</article>';
  }

  function setPanelState(panel, message) {
    const state = panel.querySelector('[data-karst-okendo-state]');
    if (!state) return;
    state.textContent = message || '';
    state.hidden = !message;
  }

  function appendReviews(panel, reviews) {
    const list = panel.querySelector('[data-karst-okendo-list]');
    const classPrefix = panel.dataset.okendoClassPrefix || 'karst-pdp-atf';
    if (!list) return;
    list.insertAdjacentHTML('beforeend', reviews.map(function(review) {
      return reviewCard(review, classPrefix);
    }).join(''));
  }

  function updatePanel(panel, aggregate, reviewsData) {
    const count = Number(aggregate?.reviewCount || 0);
    if (!count) {
      panel.hidden = true;
      return;
    }

    const title = panel.querySelector('[data-karst-okendo-panel-title]');
    const loadMore = panel.querySelector('[data-karst-okendo-load-more]');
    const loader = panel.querySelector('[data-karst-okendo-loader]');
    const reviews = Array.isArray(reviewsData?.reviews) ? reviewsData.reviews : [];
    panel.dataset.okendoNextUrl = reviewsData?.nextUrl || '';
    if (title) title.textContent = 'Reviews (' + count + ')';

    const list = panel.querySelector('[data-karst-okendo-list]');
    if (list) list.innerHTML = '';
    appendReviews(panel, reviews);
    setPanelState(panel, reviews.length ? '' : 'No reviews found');
    if (loadMore) loadMore.hidden = !panel.dataset.okendoNextUrl;
    if (loader) loader.hidden = true;
    panel.removeAttribute('data-okendo-loading');
    panel.setAttribute('aria-busy', 'false');
    panel.hidden = false;
  }

  function bindPanel(panel) {
    if (panel.dataset.okendoBound === 'true') return;
    panel.dataset.okendoBound = 'true';

    panel.addEventListener('click', function(event) {
      const readMore = event.target.closest('[data-karst-okendo-read-more]');
      if (readMore) {
        const body = readMore.closest('[data-karst-okendo-review-body]');
        if (body) body.textContent = body.dataset.fullBody || body.textContent;
        return;
      }

      const loadMore = event.target.closest('[data-karst-okendo-load-more]');
      if (!loadMore) return;
      const nextUrl = panel.dataset.okendoNextUrl || '';
      if (!nextUrl) return;
      const loader = panel.querySelector('[data-karst-okendo-loader]');

      panel.setAttribute('data-okendo-loading', 'true');
      panel.setAttribute('aria-busy', 'true');
      loadMore.disabled = true;
      loadMore.hidden = true;
      loadMore.setAttribute('aria-hidden', 'true');
      if (loader) loader.hidden = false;
      fetchJson(BASE_URL + nextUrl)
        .then(function(data) {
          const prepared = prepareReviewsData(data || {});
          appendReviews(panel, prepared.reviews || []);
          panel.dataset.okendoNextUrl = prepared.nextUrl || '';
          loadMore.hidden = !panel.dataset.okendoNextUrl;
        })
        .finally(function() {
          panel.removeAttribute('data-okendo-loading');
          panel.setAttribute('aria-busy', 'false');
          loadMore.disabled = false;
          loadMore.setAttribute('aria-hidden', panel.dataset.okendoNextUrl ? 'false' : 'true');
          if (loader) loader.hidden = true;
          loadMore.hidden = !panel.dataset.okendoNextUrl;
        });
    });
  }

  function initOkendoReviews(root) {
    const nodes = Array.from((root || document).querySelectorAll('[data-karst-okendo-summary], [data-karst-okendo-panel]'));
    if (!nodes.length) return;

    const groups = new Map();
    nodes.forEach(function(node) {
      const clientId = node.dataset.okendoClientId || '';
      const productId = node.dataset.okendoProductId || '';
      if (!clientId || !productId) return;
      const key = clientId + ':' + normalizedProductId(productId);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(node);
      if (node.matches('[data-karst-okendo-panel]')) bindPanel(node);
    });

    groups.forEach(function(groupNodes) {
      const first = groupNodes[0];
      fetchProductReviews(first.dataset.okendoClientId, first.dataset.okendoProductId)
        .then(function(data) {
          groupNodes.forEach(function(node) {
            if (node.matches('[data-karst-okendo-summary]')) updateSummary(node, data.aggregate);
            if (node.matches('[data-karst-okendo-panel]')) updatePanel(node, data.aggregate, data.reviewsData);
          });
        });
    });
  }

  document.addEventListener('DOMContentLoaded', function() {
    initOkendoReviews(document);
  });
  document.addEventListener('shopify:section:load', function(event) {
    initOkendoReviews(event.target);
  });

  window.karstInitOkendoReviews = initOkendoReviews;
})();
